import React, { useState } from 'react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { calculateWeightedMetrics, calculateSimilarity, exportToCSV } from '../utils/comparisonMetrics';
import './Modal.css';
import './CompareAnalysisModal.css';

// Register Chart.js components
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const CompareAnalysisModal = ({ isOpen, onClose, currentOriginsData, currentAnalysisName, savedAnalyses, loading }) => {
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [comparisonView, setComparisonView] = useState(false);

  if (!isOpen) return null;

  const handleSelectAnalysis = (analysis) => {
    setSelectedAnalysis(analysis);
    setComparisonView(true);
  };

  const handleBack = () => {
    setComparisonView(false);
    setSelectedAnalysis(null);
  };

  const handleClose = () => {
    setComparisonView(false);
    setSelectedAnalysis(null);
    onClose();
  };

  // Selection view
  if (!comparisonView) {
    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Compare Analysis</h2>
            <button className="modal-close" onClick={handleClose}>×</button>
          </div>

          <div className="modal-body">
            <p className="modal-description">
              Select a saved analysis to compare with your current analysis
            </p>

            {loading ? (
              <div className="empty-state">
                <p>Loading saved analyses...</p>
              </div>
            ) : savedAnalyses.length === 0 ? (
              <div className="empty-state">
                <p>No saved analyses available</p>
                <p className="empty-state-hint">Save an analysis first to enable comparisons</p>
              </div>
            ) : (
              <div className="analyses-list">
                {savedAnalyses.map(analysis => (
                  <div
                    key={analysis.id}
                    className="analysis-item"
                    onClick={() => handleSelectAnalysis(analysis)}
                  >
                    <div className="analysis-item-header">
                      <h3>{analysis.analysis_name}</h3>
                    </div>
                    <div className="analysis-item-meta">
                      <span className="analysis-date">
                        {new Date(analysis.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    {analysis.origins_data && (
                      <div className="analysis-item-stats">
                        <span>{analysis.origins_data.total_origins || analysis.origins_data.origins?.length || 0} origin sectors</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Comparison view
  return (
    <ComparisonView
      currentOriginsData={currentOriginsData}
      currentAnalysisName={currentAnalysisName}
      selectedAnalysis={selectedAnalysis}
      onBack={handleBack}
      onClose={handleClose}
    />
  );
};

const ComparisonView = ({ currentOriginsData, currentAnalysisName, selectedAnalysis, onBack, onClose }) => {
  // Calculate metrics for both analyses
  const activeMetrics = calculateWeightedMetrics(currentOriginsData);
  const savedMetrics = calculateWeightedMetrics(selectedAnalysis.origins_data);

  if (!activeMetrics || !savedMetrics) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Comparison Error</h2>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <p>Unable to calculate metrics for comparison. Origin data may be missing.</p>
          </div>
          <div className="modal-footer">
            <button onClick={onBack} className="btn-secondary">Back</button>
            <button onClick={onClose} className="btn-primary">Close</button>
          </div>
        </div>
      </div>
    );
  }

  const handleExportCSV = () => {
    exportToCSV(activeMetrics, savedMetrics, currentAnalysisName || 'Active Analysis', selectedAnalysis.analysis_name, similarityScores);
  };

  // Normalize spend data for comparison (convert to relative percentages)
  const normalizeSpendData = (spendData) => {
    const total = Object.values(spendData).reduce((sum, val) => sum + val, 0);
    if (total === 0) return {};
    const normalized = {};
    Object.entries(spendData).forEach(([key, val]) => {
      normalized[key] = (val / total) * 100;
    });
    return normalized;
  };

  // Calculate similarity scores for each category
  const similarityScores = {
    'Age Profile': calculateSimilarity(activeMetrics.profile.ageProfiles, savedMetrics.profile.ageProfiles),
    'Income Profile': calculateSimilarity(activeMetrics.profile.incomeProfiles, savedMetrics.profile.incomeProfiles),
    'Social Grade': calculateSimilarity(activeMetrics.socialGrade, savedMetrics.socialGrade),
    'Tenure': calculateSimilarity(activeMetrics.tenure, savedMetrics.tenure),
    'Employment': calculateSimilarity(activeMetrics.employment, savedMetrics.employment),
    'Education': calculateSimilarity(activeMetrics.education, savedMetrics.education),
    'IMD Decile': calculateSimilarity(activeMetrics.imd, savedMetrics.imd)
  };

  // Add spend similarity if data exists
  if (activeMetrics.spend && savedMetrics.spend &&
      Object.keys(activeMetrics.spend.totalSpend).length > 0 &&
      Object.keys(savedMetrics.spend.totalSpend).length > 0) {
    similarityScores['Total Spend'] = calculateSimilarity(
      normalizeSpendData(activeMetrics.spend.totalSpend),
      normalizeSpendData(savedMetrics.spend.totalSpend)
    );
    similarityScores['Spend per Person'] = calculateSimilarity(
      normalizeSpendData(activeMetrics.spend.spendPerPerson),
      normalizeSpendData(savedMetrics.spend.spendPerPerson)
    );
  }

  // Prepare data for summary chart
  const summaryChartData = {
    labels: Object.keys(similarityScores),
    datasets: [
      {
        label: 'Similarity',
        data: Object.values(similarityScores),
        backgroundColor: 'rgba(102, 126, 234, 0.2)',
        borderColor: 'rgba(102, 126, 234, 1)',
        borderWidth: 2,
        pointBackgroundColor: 'rgba(102, 126, 234, 1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(102, 126, 234, 1)',
      }
    ]
  };

  const summaryChartOptions = {
    scales: {
      r: {
        beginAtZero: true,
        max: 100,
        ticks: {
          stepSize: 20,
          color: '#bdc3c7',
          backdropColor: 'transparent',
          callback: function(value) {
            return value + '%';
          }
        },
        grid: {
          color: '#4a5f7f'
        },
        pointLabels: {
          color: '#ecf0f1',
          font: {
            size: 12,
            weight: '500'
          }
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `Similarity: ${context.parsed.r.toFixed(1)}%`;
          }
        }
      }
    },
    maintainAspectRatio: true,
    responsive: true
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-comparison" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Audience Comparison of {currentAnalysisName || 'Active Analysis'} to {selectedAnalysis.analysis_name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body comparison-body">
          <div className="comparison-summary">
            <div className="summary-stat">
              <strong>Active Hex Origin Sectors:</strong> {activeMetrics.totalOrigins}
            </div>
            <div className="summary-stat">
              <strong>Comparison Hex Origin Sectors:</strong> {savedMetrics.totalOrigins}
            </div>
          </div>

          {/* Summary Similarity Chart */}
          <div className="summary-chart-container">
            <h3 className="summary-chart-title">Overall Similarity by Category</h3>
            <p style={{ marginBottom: '1rem', lineHeight: '1.6', color: '#ecf0f1' }}>
              The overall similarity of <strong>{currentAnalysisName || 'Active Analysis'}</strong> vs <strong>{selectedAnalysis.analysis_name}</strong> is: <strong>{Math.round((Object.values(similarityScores).reduce((sum, val) => sum + Math.round(val), 0) / (Object.keys(similarityScores).length * 100)) * 100)}%</strong> ({Object.values(similarityScores).reduce((sum, val) => sum + Math.round(val), 0)}/{Object.keys(similarityScores).length * 100}).
            </p>
            <p style={{ marginBottom: '1.5rem', lineHeight: '1.6', color: '#ecf0f1' }}>
              Each category is shown on the chart below, the closer to the outside edge a point is, the more alike the audiences are in that category. The closer to the middle a point is, the more the two audiences are distinct from each other. The charts below this show for each category what the similarities and differences are.
            </p>
            <div className="summary-chart-wrapper">
              <Radar data={summaryChartData} options={summaryChartOptions} />
            </div>
          </div>

          <ComparisonRow
            title="Age Profile"
            activeData={activeMetrics.profile.ageProfiles}
            savedData={savedMetrics.profile.ageProfiles}
          />

          <ComparisonRow
            title="Income Profile"
            activeData={activeMetrics.profile.incomeProfiles}
            savedData={savedMetrics.profile.incomeProfiles}
          />

          <ComparisonRow
            title="Social Grade"
            activeData={activeMetrics.socialGrade}
            savedData={savedMetrics.socialGrade}
          />

          <ComparisonRow
            title="Tenure"
            activeData={activeMetrics.tenure}
            savedData={savedMetrics.tenure}
          />

          <ComparisonRow
            title="Employment"
            activeData={activeMetrics.employment}
            savedData={savedMetrics.employment}
          />

          <ComparisonRow
            title="Education"
            activeData={activeMetrics.education}
            savedData={savedMetrics.education}
          />

          <ComparisonRow
            title="IMD Decile"
            activeData={activeMetrics.imd}
            savedData={savedMetrics.imd}
          />

          {activeMetrics.spend && savedMetrics.spend &&
           Object.keys(activeMetrics.spend.totalSpend).length > 0 &&
           Object.keys(savedMetrics.spend.totalSpend).length > 0 && (
            <>
              <SpendComparisonRow
                title="Total Spend Distribution"
                activeData={activeMetrics.spend.totalSpend}
                savedData={savedMetrics.spend.totalSpend}
              />

              <SpendComparisonRow
                title="Spend per Person Distribution"
                activeData={activeMetrics.spend.spendPerPerson}
                savedData={savedMetrics.spend.spendPerPerson}
              />
            </>
          )}
        </div>

        <div className="modal-footer comparison-footer">
          <button onClick={onBack} className="btn-secondary">Back</button>
          <button onClick={handleExportCSV} className="btn-secondary">Export CSV</button>
          <button onClick={onClose} className="btn-primary">Close</button>
        </div>
      </div>
    </div>
  );
};

const ComparisonRow = ({ title, activeData, savedData }) => {
  const similarity = calculateSimilarity(activeData, savedData);

  const labels = Object.keys(activeData);
  const activeValues = Object.values(activeData);
  const savedValues = Object.values(savedData);

  // Calculate dynamic max: round up to nearest 10 above the highest value in either dataset
  const maxActiveValue = Math.max(...activeValues);
  const maxSavedValue = Math.max(...savedValues);
  const overallMax = Math.max(maxActiveValue, maxSavedValue);
  const dynamicMax = Math.ceil(overallMax / 10) * 10;
  const stepSize = dynamicMax / 5; // 5 steps to the max

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Active',
        data: activeValues,
        backgroundColor: 'rgba(102, 126, 234, 0.2)',
        borderColor: 'rgba(102, 126, 234, 1)',
        borderWidth: 2,
        pointBackgroundColor: 'rgba(102, 126, 234, 1)',
      }
    ]
  };

  const savedChartData = {
    labels,
    datasets: [
      {
        label: 'Saved',
        data: savedValues,
        backgroundColor: 'rgba(39, 174, 96, 0.2)',
        borderColor: 'rgba(39, 174, 96, 1)',
        borderWidth: 2,
        pointBackgroundColor: 'rgba(39, 174, 96, 1)',
      }
    ]
  };

  const chartOptions = {
    scales: {
      r: {
        beginAtZero: true,
        max: dynamicMax,
        ticks: {
          stepSize: stepSize,
          color: '#bdc3c7',
          backdropColor: 'transparent'
        },
        grid: {
          color: '#4a5f7f'
        },
        pointLabels: {
          color: '#ecf0f1',
          font: {
            size: 10
          }
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.label}: ${context.parsed.r.toFixed(1)}%`;
          }
        }
      }
    },
    maintainAspectRatio: true,
    responsive: true
  };

  return (
    <div className="comparison-row">
      <h3 className="comparison-row-title">{title}</h3>
      <div className="comparison-row-content">
        <div className="chart-container">
          <h4>Active</h4>
          <Radar data={chartData} options={chartOptions} />
        </div>

        <div className="similarity-container">
          <div className="similarity-score">
            <div className="similarity-percentage">{similarity.toFixed(1)}%</div>
            <div className="similarity-label">Similar</div>
          </div>
        </div>

        <div className="chart-container">
          <h4>Saved</h4>
          <Radar data={savedChartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
};

const SpendComparisonRow = ({ title, activeData, savedData }) => {
  // Normalize to percentages for similarity calculation
  const normalizeSpendData = (spendData) => {
    const total = Object.values(spendData).reduce((sum, val) => sum + val, 0);
    if (total === 0) return {};
    const normalized = {};
    Object.entries(spendData).forEach(([key, val]) => {
      normalized[key] = (val / total) * 100;
    });
    return normalized;
  };

  const activeNormalized = normalizeSpendData(activeData);
  const savedNormalized = normalizeSpendData(savedData);
  const similarity = calculateSimilarity(activeNormalized, savedNormalized);

  const labels = Object.keys(activeData);
  const activeValues = Object.values(activeNormalized);
  const savedValues = Object.values(savedNormalized);

  // Calculate dynamic max: round up to nearest 10 above the highest value in either dataset
  const maxActiveValue = Math.max(...activeValues);
  const maxSavedValue = Math.max(...savedValues);
  const overallMax = Math.max(maxActiveValue, maxSavedValue);
  const dynamicMax = Math.ceil(overallMax / 10) * 10;
  const stepSize = dynamicMax / 5; // 5 steps to the max

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Active',
        data: activeValues,
        backgroundColor: 'rgba(102, 126, 234, 0.2)',
        borderColor: 'rgba(102, 126, 234, 1)',
        borderWidth: 2,
        pointBackgroundColor: 'rgba(102, 126, 234, 1)',
      }
    ]
  };

  const savedChartData = {
    labels,
    datasets: [
      {
        label: 'Saved',
        data: savedValues,
        backgroundColor: 'rgba(39, 174, 96, 0.2)',
        borderColor: 'rgba(39, 174, 96, 1)',
        borderWidth: 2,
        pointBackgroundColor: 'rgba(39, 174, 96, 1)',
      }
    ]
  };

  const chartOptions = {
    scales: {
      r: {
        beginAtZero: true,
        max: dynamicMax,
        ticks: {
          stepSize: stepSize,
          color: '#bdc3c7',
          backdropColor: 'transparent',
          callback: function(value) {
            return value + '%';
          }
        },
        grid: {
          color: '#4a5f7f'
        },
        pointLabels: {
          color: '#ecf0f1',
          font: {
            size: 10
          }
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.label}: ${context.parsed.r.toFixed(1)}%`;
          }
        }
      }
    },
    maintainAspectRatio: true,
    responsive: true
  };

  return (
    <div className="comparison-row">
      <h3 className="comparison-row-title">{title}</h3>
      <div className="comparison-row-content">
        <div className="chart-container">
          <h4>Active</h4>
          <Radar data={chartData} options={chartOptions} />
        </div>

        <div className="similarity-container">
          <div className="similarity-score">
            <div className="similarity-percentage">{similarity.toFixed(1)}%</div>
            <div className="similarity-label">Similar</div>
          </div>
        </div>

        <div className="chart-container">
          <h4>Saved</h4>
          <Radar data={savedChartData} options={chartOptions} />
        </div>
      </div>
    </div>
  );
};

export default CompareAnalysisModal;
